import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { ColumnDef } from "@tanstack/react-table"
import HrReportTemplate from "Components/templates/hr/hr-report"
import BarChartCard from "Components/organisms/hr-reports/bar-chart-card"
import EmployeeLink from "Components/organisms/hr-reports/employee-link"
import EmployeeTable from "Components/organisms/hr-reports/employee-table"
import FilterBar from "Components/organisms/hr-reports/filter-bar"
import StatCard from "Components/organisms/hr-reports/stat-card"
import useCrossFilter, { CrossFilterDimension } from "hooks/useCrossFilter"
import { HrReportRow } from "models/hr-reports"
import { HrReportsSliceState, setAnniversaryReport } from "slices/human-resources/hr-reports/reducer"
import { getAnniversaryReport } from "slices/human-resources/hr-reports/thunk"
import { formatDate, HR_DIM, hrReportDimensions, MONTHS, yearsLabel } from "utils/hr-reports"

const selectHrReports = createSelector(
    (state: any) => state.HrReports,
    ({ anniversary }: HrReportsSliceState) => ({ anniversary })
)

// One person with an anniversary this calendar year (hired before this year)
interface AnniversaryRow extends HrReportRow {
    hireDate: string
    month: number        // 0-11, from the hire date
    day: number
    years: number        // years completed on this year's anniversary
}

const MONTH = "month"
const YEARS = "years"
const TODAY = "today"

const isLeap = (year: number) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
// 29 February anniversaries are celebrated on the 28th in non-leap years
const anniversaryDay = (month: number, day: number, year: number) => (month === 1 && day === 29 && !isLeap(year) ? 28 : day)

const monthDay = (r: AnniversaryRow) => `${MONTHS[r.month].slice(0, 3)} ${r.day}`

const text = (value: string | null) => value ?? "-"

const COLUMNS: ColumnDef<AnniversaryRow, any>[] = [
    { accessorKey: "name", header: "Employee", size: 220, cell: ({ row }) => <EmployeeLink id={row.original.id} name={row.original.name} /> },
    { accessorKey: "hireDate", header: "Hire Date", size: 130, cell: ({ getValue }) => formatDate(getValue()) },
    {
        id: "anniversary",
        header: "Anniversary",
        size: 120,
        accessorFn: r => r.month * 100 + r.day,
        cell: ({ row }) => monthDay(row.original),
    },
    { accessorKey: "years", header: "Years", size: 90 },
    { accessorKey: "jobTitle", header: "Job Title", size: 200, cell: ({ getValue }) => text(getValue()) },
    { accessorKey: "department", header: "Department", size: 200, cell: ({ getValue }) => text(getValue()) },
    { accessorKey: "manager", header: "Manager", size: 180, cell: ({ row }) => <EmployeeLink id={row.original.managerId} name={row.original.manager} /> },
]

const searchText = (r: AnniversaryRow) => [r.name, r.jobTitle, r.department, r.manager].join(" ")

const AnniversaryPage = () => {
    document.title = "Anniversary | IbexIQ"
    const dispatch: any = useDispatch()
    const { anniversary } = useSelector(selectHrReports)
    const [isLoading, setIsLoading] = useState(anniversary === null)
    const [hasError, setHasError] = useState(false)

    const load = async () => {
        setIsLoading(true)
        setHasError(false)
        const rows = await getAnniversaryReport()
        if (rows) dispatch(setAnniversaryReport(rows))
        else setHasError(true)
        setIsLoading(false)
    }

    useEffect(() => { load() }, [])

    const today = useMemo(() => new Date(), [])
    const thisYear = today.getFullYear()
    const thisMonth = today.getMonth()
    const nextMonth = (thisMonth + 1) % 12

    const people = useMemo<AnniversaryRow[]>(() => (anniversary ?? [])
        .filter(r => r.hireDate)
        .map(r => {
            const d = new Date(r.hireDate!)
            return {
                ...r,
                hireDate: r.hireDate!,
                month: d.getUTCMonth(),
                day: d.getUTCDate(),
                years: thisYear - d.getUTCFullYear(),
            }
        }), [anniversary, thisYear])

    const isToday = (r: AnniversaryRow) => r.month === thisMonth && anniversaryDay(r.month, r.day, thisYear) === today.getDate()

    // Cards are always relative to today. Next month counts people hired before that month's year
    // (in December that includes this year's hires).
    const cards = useMemo(() => {
        const nextMonthYear = thisMonth === 11 ? thisYear + 1 : thisYear
        const hireYear = (r: AnniversaryRow) => thisYear - r.years
        return {
            today: people.filter(r => r.years > 0 && isToday(r)).length,
            thisMonth: people.filter(r => r.years > 0 && r.month === thisMonth).length,
            nextMonth: people.filter(r => hireYear(r) < nextMonthYear && r.month === nextMonth).length,
            todayLabel: today.toLocaleDateString("en-CA", { month: "long", day: "numeric" }),
        }
    }, [people, today, thisYear])

    // The table and charts only list people with an anniversary this year (hired before this year)
    const withAnniversary = useMemo(() => people.filter(r => r.years > 0), [people])

    const dimensions = useMemo<CrossFilterDimension<AnniversaryRow>[]>(() => [
        ...hrReportDimensions<AnniversaryRow>(),
        { key: MONTH, label: "Month", values: r => [MONTHS[r.month]] },
        { key: YEARS, label: "Years of Service", values: r => [yearsLabel(r.years)] },
        { key: TODAY, label: "Today", values: r => [isToday(r) ? "Yes" : "No"] },
    ], [])
    const filter = useCrossFilter<AnniversaryRow>(withAnniversary, dimensions)

    const yearOptions = useMemo(() => Array.from(new Set(withAnniversary.map(r => r.years)))
        .sort((a, b) => a - b)
        .map(yearsLabel), [withAnniversary])

    const monthCounts = useMemo(() => {
        const counts = filter.countsFor(MONTH)
        return MONTHS.map(m => ({ value: m, count: counts.find(c => c.value === m)?.count ?? 0 }))
    }, [filter])
    const yearCounts = filter.countsFor(YEARS).sort((a, b) => parseInt(a.value) - parseInt(b.value))

    const selectedMonth = filter.selectedFor(MONTH)
    const isTodayActive = filter.selectedFor(TODAY).includes("Yes")
    const isMonthActive = (m: number) => !isTodayActive && selectedMonth.length === 1 && selectedMonth[0] === MONTHS[m]

    // A card filters the page by its period; clicking it again clears it
    const pickToday = () => {
        filter.clear(MONTH)
        filter.select(TODAY, isTodayActive ? [] : ["Yes"])
    }
    const pickMonth = (m: number) => {
        filter.clear(TODAY)
        filter.select(MONTH, isMonthActive(m) ? [] : [MONTHS[m]])
    }

    return <HrReportTemplate
        title="Anniversary"
        manual="human-resources/anniversary"
        isLoading={isLoading && anniversary === null}
        hasError={hasError && anniversary === null}
        onRetry={load}
    >
        <FilterBar
            filter={filter}
            dropdowns={[
                { key: MONTH, label: "Month", single: true, options: MONTHS },
                { key: YEARS, label: "Years of Service", options: yearOptions },
                { key: HR_DIM.department, label: "Department" },
                { key: HR_DIM.manager, label: "Manager" },
            ]}
        />

        <div className="hr-report-stats">
            <StatCard label="Today's anniversaries" value={cards.today} caption={cards.todayLabel} active={isTodayActive} onClick={pickToday} />
            <StatCard label="This month's anniversaries" value={cards.thisMonth} caption={MONTHS[thisMonth]} active={isMonthActive(thisMonth)} onClick={() => pickMonth(thisMonth)} />
            <StatCard label="Next month's anniversaries" value={cards.nextMonth} caption={MONTHS[nextMonth]} active={isMonthActive(nextMonth)} onClick={() => pickMonth(nextMonth)} />
        </div>

        <div className="hr-report-grid">
            <BarChartCard
                title="Anniversaries by Month"
                data={monthCounts}
                order="label"
                selected={selectedMonth}
                onSelect={(value, additive) => filter.toggle(MONTH, value, additive)}
                onClear={() => filter.clear(MONTH)}
            />
            <BarChartCard
                title="Employees by Years of Service"
                data={yearCounts}
                order="label"
                selected={filter.selectedFor(YEARS)}
                onSelect={(value, additive) => filter.toggle(YEARS, value, additive)}
                onClear={() => filter.clear(YEARS)}
            />
        </div>

        <EmployeeTable
            title="Anniversaries"
            report="Anniversary"
            rows={filter.filtered}
            columns={COLUMNS}
            searchText={searchText}
            initialSorting={[{ id: "anniversary", desc: false }]}
        />
    </HrReportTemplate>
}

export default AnniversaryPage
